import { Component, Inject, OnInit } from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    FormArray,
    Validators,
    ValidationErrors,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '../../shared/services/snackbar.service';
import { EmailDomainAuthService } from './email-domain-dialog.service';

@Component({
    selector: 'app-email-domain-dialog',
    templateUrl: './email-domain-dialog.component.html',
    styleUrls: ['./email-domain-dialog.component.scss'],
})
export class EmailDomainDialogComponent implements OnInit {
    configForm!: FormGroup;
    spinner = false;
    maxInputs = 5;
    isFetchingConfig = true;

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<EmailDomainDialogComponent>,
        private emailAuditorService: EmailDomainAuthService,
        private snackBar: SnackbarService,
        @Inject(MAT_DIALOG_DATA) public data: { pressroomId: string },
    ) {}

    ngOnInit(): void {
        this.configForm = this.fb.group({
            all: [false],
            allowedEmailsDomains: this.fb.array<FormControl<string>>(
                [],
                Validators.required,
            ),
        });

        this.spinner = true;
        this.emailAuditorService.getConfig(this.data.pressroomId).subscribe({
            next: (res: any) => {
                const config = res?.webConfig?.emailAuditorDomainAuth;
                const all = config?.all ?? false;
                const domains: string[] = config?.allowedEmailsDomains ?? [];

                this.configForm.get('all')?.setValue(all);

                if (domains.length > 0) {
                    domains.forEach((domain) =>
                        this.emailDomainsArray.push(
                            new FormControl<string>(domain, {
                                validators: [
                                    Validators.required,
                                    this.emailDomainValidator,
                                ],
                                nonNullable: true,
                            }),
                        ),
                    );
                } else {
                    this.addField();
                }

                this.toggleDomainControls(all);
                this.spinner = false;
                this.isFetchingConfig = false;
            },
            error: (err) => {
                console.error(err);
                this.snackBar.showMessageWithDelay(
                    'Failed to load email domain configuration!',
                    3000,
                );
                this.spinner = false;
                this.isFetchingConfig = false;
            },
        });

        this.configForm.get('all')?.valueChanges.subscribe((value: boolean) => {
            this.toggleDomainControls(value);
        });
    }

    get emailDomainsArray(): FormArray<FormControl<string>> {
        return this.configForm.get('allowedEmailsDomains') as FormArray<
            FormControl<string>
        >;
    }

    toggleDomainControls(disabled: boolean) {
        if (disabled) {
            for (let i = this.emailDomainsArray.length - 1; i >= 0; i--) {
                const ctrl = this.emailDomainsArray.at(i);
                const value = ctrl.value?.trim();

                if (!value) {
                    this.emailDomainsArray.removeAt(i);
                } else {
                    ctrl.disable(); // valid filled -> keep but disable
                }
            }

            // If nothing left (all were empty), allow at least one disabled box
            if (this.emailDomainsArray.length === 0) {
                const newCtrl = new FormControl<string>('', {
                    validators: [
                        Validators.required,
                        this.emailDomainValidator,
                    ],
                    nonNullable: true,
                });
                newCtrl.disable();
                this.emailDomainsArray.push(newCtrl);
            }
        } else {
            this.emailDomainsArray.controls.forEach((ctrl) => ctrl.enable());
        }
    }

    addField() {
        if (
            this.emailDomainsArray.length < this.maxInputs &&
            this.emailDomainsArray.controls.every((ctrl) => ctrl.valid)
        ) {
            this.emailDomainsArray.push(
                new FormControl<string>('', {
                    validators: [
                        Validators.required,
                        this.emailDomainValidator,
                    ],
                    nonNullable: true,
                }),
            );
        }
    }

    removeField(index: number) {
        if (this.emailDomainsArray.length > 1) {
            this.emailDomainsArray.removeAt(index);
        }
    }

    emailDomainValidator(
        control: FormControl<string>,
    ): ValidationErrors | null {
        const value = control.value;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const domainRegex = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(value) && !domainRegex.test(value)) {
            return { invalid: true };
        }
        return null;
    }

    submitConfig() {
        const all = this.configForm.get('all')?.value;

        const validDomains = this.emailDomainsArray.controls
            .map((ctrl) => ctrl.value?.trim())
            .filter((val) => !!val); // remove empty strings/nulls

        const payload = {
            all,
            allowedEmailsDomains: all
                ? validDomains
                : this.emailDomainsArray.getRawValue(),
        };

        this.spinner = true;

        this.emailAuditorService
            .updateConfig(this.data.pressroomId, payload)
            .subscribe({
                next: () => {
                    this.snackBar.showMessageWithDelay(
                        'Email auditor configuration updated!',
                        3000,
                    );
                    this.dialogRef.close(true);
                    this.spinner = false;
                },
                error: (err: any) => {
                    console.error(err);
                    this.snackBar.showMessageWithDelay(
                        'Failed to update email auditor configuration!',
                        3000,
                    );
                    this.spinner = false;
                },
            });
    }

    close(): void {
        this.dialogRef.close();
    }
}
